import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import './BillingRateTable.css';

const EMPTY_ROW = {
  prefix: '', country: '',
  marketing_rate_usd: '', marketing_markup_percent: '',
  utility_rate_usd: '', utility_markup_percent: '',
  authentication_rate_usd: '', authentication_markup_percent: '',
  service_tokens: '',
};
const CATEGORIES = [
  ['marketing', 'Marketing'],
  ['utility', 'Utility'],
  ['authentication', 'Authentication'],
];
const PREVIEW_FIELDS = [
  ['country', 'Market'],
  ['marketing_rate_usd', 'Marketing USD'],
  ['marketing_markup_percent', 'Marketing markup'],
  ['utility_rate_usd', 'Utility USD'],
  ['utility_markup_percent', 'Utility markup'],
  ['authentication_rate_usd', 'Authentication USD'],
  ['authentication_markup_percent', 'Authentication markup'],
  ['service_tokens', 'Service tokens'],
];

function previewValue(field, value) {
  if (value === null || value === undefined || value === '') return 'global default';
  if (field.endsWith('_rate_usd')) return `$${Number(value).toFixed(4)}`;
  if (field.endsWith('_markup_percent')) return `${value}%`;
  return String(value);
}

function tokenCharge(row, category, settings, accountMargin) {
  if (category === 'service') return Number(row.service_tokens || settings.response_tokens || 1);
  const rate = Number(row[`${category}_rate_usd`] || 0);
  const rowMargin = row[`${category}_markup_percent`];
  const margin = accountMargin ?? (rowMargin === '' || rowMargin == null
    ? Number(settings.default_margin_percent || 0) : Number(rowMargin));
  return Math.max(1, Math.ceil(Number(
    (rate * (1 + margin / 100) * Number(settings.tokens_per_usd || 1)).toFixed(8)
  )));
}

export default function BillingRateTable({ settings, accountMargin = null }) {
  const [rows, setRows] = useState([]);
  const [revision, setRevision] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importCsv, setImportCsv] = useState('');
  const [importName, setImportName] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/billing/rates');
      setRows(data.rows || []);
      setRevision(data.revision);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load token pricing');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter(row =>
    `${row.prefix} ${row.country}`.toLowerCase().includes(search.trim().toLowerCase())
  ), [rows, search]);

  const saveRow = async () => {
    setBusy(true);
    try {
      const prefix = String(editing.prefix || '').trim().toUpperCase();
      if (!editing._existing && rows.some(row => row.prefix === prefix)) {
        toast.error('This prefix already exists. Select its Edit button.');
        return;
      }
      const { data } = await api.put(`/admin/billing/rates/${encodeURIComponent(prefix)}`, {
        ...editing, prefix, revision,
      });
      setRevision(data.revision);
      setEditing(null);
      setPreview(null);
      await load();
      toast.success('Pricing row saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save pricing row');
      if (err.response?.status === 409) await load();
    } finally { setBusy(false); }
  };

  const exportRates = async () => {
    try {
      const response = await api.get('/admin/billing/rates/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'whatsapp-token-pricing.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error('Could not export token pricing'); }
  };

  const readCsv = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setPreview(null);
    setImportCsv('');
    if (!file) return;
    if (file.size > 200000) return toast.error('CSV file must be 200 KB or smaller');
    try {
      const csv = await file.text();
      setImportCsv(csv);
      setImportName(file.name);
      setBusy(true);
      const { data } = await api.post('/admin/billing/rates/import-preview', { csv });
      setPreview(data);
    } catch (err) {
      const message = err.response?.data?.error || 'Could not preview CSV';
      setPreview({
        revision: null, changes: [], errors: [message],
        counts: { added: 0, changed: 0, unchanged: 0 },
      });
      toast.error(message);
    } finally { setBusy(false); }
  };

  const applyImport = async () => {
    if (!preview || preview.errors?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/billing/rates/import', {
        csv: importCsv, revision: preview.revision,
      });
      toast.success(`Pricing imported: ${data.added} added, ${data.changed} changed`);
      setPreview(null);
      setImportCsv('');
      setImportName('');
      setEditing(null);
      await load();
    } catch (err) {
      const messages = err.response?.data?.errors;
      toast.error(messages?.[0] || err.response?.data?.error || 'Could not import CSV');
      if (err.response?.status === 409) { setPreview(null); await load(); }
    } finally { setBusy(false); }
  };

  return (
    <section className="billing-rate-section" aria-label="Country and category pricing">
      <div className="billing-rate-heading">
        <div>
          <h3>Country code pricing</h3>
          <p>USD rates and markup for templates; service replies use tokens per message. Blank markup and service values inherit the global settings above.</p>
        </div>
        <div className="billing-rate-heading-actions">
          <button className="btn btn-ghost" onClick={exportRates}>Export CSV</button>
          <label className="btn btn-ghost billing-rate-import">
            Import CSV
            <input type="file" accept=".csv,text/csv" onChange={readCsv} disabled={busy} />
          </label>
          <button className="btn btn-primary" onClick={() => setEditing({ ...EMPTY_ROW, _existing: false })}>Add prefix</button>
        </div>
      </div>

      <input className="input billing-rate-search" value={search}
        onChange={event => setSearch(event.target.value)} placeholder="Search country or dialing prefix" />
      <div className="table-wrap billing-rate-table">
        <table>
          <thead><tr>
            <th>Prefix</th><th>Market</th><th>Marketing</th><th>Utility</th><th>Authentication</th>
            <th>Service</th><th>Token preview</th><th></th>
          </tr></thead>
          <tbody>{filtered.map(row => (
            <tr key={row.prefix}>
              <td><strong>{row.prefix === 'OTHER' ? 'Other' : `+${row.prefix}`}</strong></td>
              <td>{row.country}</td>
              {CATEGORIES.map(([key]) => (
                <td key={key}>
                  <strong>${Number(row[`${key}_rate_usd`]).toFixed(4)}</strong>
                  <div className="billing-rate-muted">{row[`${key}_markup_percent`] == null
                    ? `Default ${settings.default_margin_percent}%`
                    : `+${row[`${key}_markup_percent`]}%`}</div>
                </td>
              ))}
              <td>{row.service_tokens == null ? `Default ${settings.response_tokens}` : row.service_tokens} tokens</td>
              <td className="billing-rate-preview">
                M {tokenCharge(row, 'marketing', settings, accountMargin)} ·
                U {tokenCharge(row, 'utility', settings, accountMargin)} ·
                A {tokenCharge(row, 'authentication', settings, accountMargin)} ·
                S {tokenCharge(row, 'service', settings, accountMargin)}
              </td>
              <td><button className="btn btn-ghost btn-sm" onClick={() => setEditing({
                ...row,
                _existing: true,
                marketing_markup_percent: row.marketing_markup_percent ?? '',
                utility_markup_percent: row.utility_markup_percent ?? '',
                authentication_markup_percent: row.authentication_markup_percent ?? '',
                service_tokens: row.service_tokens ?? '',
              })}>Edit</button></td>
            </tr>
          ))}</tbody>
        </table>
        {!filtered.length && <div className="billing-rate-empty">No matching country codes.</div>}
      </div>
      <p className="billing-rate-muted">Token preview: M marketing, U utility, A authentication, S service{accountMargin != null ? ` · selected workspace margin: ${accountMargin}%` : ''}. The longest matching prefix is used.</p>

      {editing && (
        <div className="billing-rate-editor">
          <div className="billing-rate-editor-head">
            <h4>{editing._existing ? `Edit ${editing.prefix === 'OTHER' ? 'Other' : `+${editing.prefix}`}` : 'Add dialing prefix'}</h4>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Close</button>
          </div>
          <div className="billing-rate-editor-grid">
            <label className="label">Dialing prefix
              <input className="input" value={editing.prefix}
                disabled={editing._existing}
                onChange={event => setEditing(row => ({ ...row, prefix: event.target.value }))} placeholder="e.g. 27" />
            </label>
            <label className="label">Market label
              <input className="input" value={editing.country}
                onChange={event => setEditing(row => ({ ...row, country: event.target.value }))} />
            </label>
            {CATEGORIES.map(([key, label]) => (
              <React.Fragment key={key}>
                <label className="label">{label} USD rate
                  <input className="input" type="number" min="0" max="100" step="0.000001"
                    value={editing[`${key}_rate_usd`]}
                    onChange={event => setEditing(row => ({ ...row, [`${key}_rate_usd`]: event.target.value }))} />
                </label>
                <label className="label">{label} markup %
                  <input className="input" type="number" min="0" max="1000" step="0.1"
                    placeholder="Global default" value={editing[`${key}_markup_percent`]}
                    onChange={event => setEditing(row => ({ ...row, [`${key}_markup_percent`]: event.target.value }))} />
                </label>
              </React.Fragment>
            ))}
            <label className="label">Service tokens per reply
              <input className="input" type="number" min="1" max="1000000" step="1"
                placeholder="Global default" value={editing.service_tokens}
                onChange={event => setEditing(row => ({ ...row, service_tokens: event.target.value }))} />
            </label>
          </div>
          <div className="billing-rate-draft-preview">
            Preview: marketing {tokenCharge(editing, 'marketing', settings, accountMargin)} ·
            utility {tokenCharge(editing, 'utility', settings, accountMargin)} ·
            authentication {tokenCharge(editing, 'authentication', settings, accountMargin)} ·
            service {tokenCharge(editing, 'service', settings, accountMargin)} tokens
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={saveRow}>Save pricing row</button>
        </div>
      )}

      {preview && (
        <div className="billing-rate-import-preview">
          <h4>Review {importName}</h4>
          <p>{preview.counts.added} added · {preview.counts.changed} changed · {preview.counts.unchanged} unchanged</p>
          {preview.errors?.length > 0 && <div className="alert alert-danger" role="alert">
            <strong>Fix these errors before importing:</strong>
            <ul>{preview.errors.map((error, index) => <li key={index}>{error}</li>)}</ul>
          </div>}
          <div className="billing-rate-change-list">
            {preview.changes.filter(change => change.kind !== 'unchanged').map(change => (
              <div key={change.prefix}>
                <strong>{change.kind === 'added' ? 'Add' : 'Change'} {change.prefix === 'OTHER' ? 'Other' : `+${change.prefix}`}</strong>
                <div>{PREVIEW_FIELDS.filter(([field]) =>
                  !change.before || (change.before[field] ?? null) !== (change.after[field] ?? null)
                ).map(([field, label]) => (
                  <div key={field}>
                    {label}: {change.before ? `${previewValue(field, change.before[field])} → ` : ''}
                    {previewValue(field, change.after[field])}
                  </div>
                ))}</div>
              </div>
            ))}
          </div>
          <div className="billing-rate-heading-actions">
            <button className="btn btn-ghost" onClick={() => { setPreview(null); setImportCsv(''); }}>Cancel</button>
            <button className="btn btn-primary" disabled={busy || preview.errors?.length > 0}
              onClick={applyImport}>Apply CSV changes</button>
          </div>
        </div>
      )}
    </section>
  );
}
